import { Attachment, UUID } from '@/types/types';
import { ApiQueryKey } from '@/configs/ApiQueryKey';
import { FormTemplate } from './FormTemplateApi';
import { CreateProductTypeForm } from '@/pages/authenticated/productTypes/CreateProductType';
import { EditProductTypeForm } from '@/pages/authenticated/productTypes/EditProductType';
import { blankProductTypeContent } from '@/utils/utilities';
import httpClient from './httpClient';
import EntityApi from './EntityApi';
import AppApi from './AppApi';
import { Relation } from '@/store/slices/formTemplateSlice';
import { ConfiguratorProductSpecification } from './ConfiguratorProductDataApi';

export enum SectionType {
	CARDS = 'CARDS',
	LISTS = 'LISTS',
	ARTICLES = 'ARTICLES',
}

interface ProductTypeSection {
	title: string;
	description: string;
}

export interface ProductTypeSectionCard extends ProductTypeSection {
	cards: { title: string; description: string }[];
}

export interface ProductTypeSectionLists extends ProductTypeSection {
	lists: { title: string; entries: string[] }[];
}

export interface ProductTypeSectionArticles extends ProductTypeSection {
	articles: { title: string; description: string; image: Attachment | null }[];
}

export interface PredefinedDimension {
	width: number;
	height: number;
	depth: number;
	relations: Relation[] | undefined;
}

export interface ProductType {
	id: UUID;
	name: string;
	description: string;
	/** Description shown in the creator; forms use `description`. */
	creatorDescription?: string | null;
	image?: Attachment;
	formTemplate?: FormTemplate;
	listOrder: number;
	isPerDimensionQuantityEnabled: boolean;
	predefinedDimensions: PredefinedDimension[];
	minCustomDimensionQuantity: number;
	content: {
		[SectionType.CARDS]: ProductTypeSectionCard;
		[SectionType.LISTS]: ProductTypeSectionLists;
		[SectionType.ARTICLES]: ProductTypeSectionArticles;
	};
	isHidden: boolean;
	minQuantity?: number;
}

class ProductTypeApi extends EntityApi<ProductType> {
	constructor() {
		super(ApiQueryKey.productTypes);
		this.createWithImage = this.createWithImage.bind(this);
		this.updateWithImage = this.updateWithImage.bind(this);
	}

	async createWithImage(
		payload: CreateProductTypeForm & {
			description: string;
			creatorDescription?: string | null;
			specification?: Omit<ConfiguratorProductSpecification, 'productType'>;
		}
	) {
		let image: Attachment | undefined;

		if (payload.file) image = await AppApi.createAttachment(payload.file);

		const response = await httpClient.post<ProductType>(this.queryKey, {
			...payload,
			image,
			content: blankProductTypeContent(),
		});

		if (payload.specification) {
			try {
				await httpClient.put(`configurator/product-specifications/${response.data.id}` as any, payload.specification);
			} catch (_) {}
		}

		return response.data;
	}

	async updateWithImage(
		payload: EditProductTypeForm & {
			description: string;
			creatorDescription?: string | null;
			specification?: Omit<ConfiguratorProductSpecification, 'productType'>;
		}
	) {
		let image: Attachment | undefined;

		if (payload.file) image = await AppApi.createAttachment(payload.file);

		const response = await httpClient.patch<ProductType>(`${this.queryKey}/${payload.id}`, {
			...payload,
			image,
		});

		if (payload.specification) {
			try {
				await httpClient.put(`configurator/product-specifications/${payload.id}` as any, payload.specification);
			} catch (_) {}
		}
		return response.data;
	}
}

export default new ProductTypeApi();
